import { useEffect, useRef, useState } from "react";
import {
  getFlow,
  getParameters,
  getPromptTemplateVersions,
  forkVersion,
  unpinEnvironment,
} from "../api";
import {
  Title,
  Container,
  Loader,
  Tabs,
  rem,
  Center,
  Group,
  Grid,
  Modal,
  Button,
  Text,
  Badge,
  Collapse,
  ActionIcon,
} from "@mantine/core";
import {
  IconDashboard,
  IconDirections,
  IconGitBranch,
  IconGitCompare,
  IconMessageCircle,
  IconPlayerSkipForwardFilled,
  IconSettings,
  IconHierarchy3,
} from "@tabler/icons-react";
import FlowSettingsBase from "./FlowSettingsBase";
import FlowDetailSampleRate from "./FlowDetailSampleRate";
import FlowRun from "./FlowRun";
import FlowVersionEditor from "./FlowEditor";
import FlowTemplateVersions from "./FlowTemplateVersions";
import { useDisclosure } from "@mantine/hooks";
import FlowEnvironments from "./FlowEnvironments";
import FlowAnalysis from "./FlowAnalysis";
import { notifications } from "@mantine/notifications";
import useFlowStore from "./flowStore";
import AgentGraphPreview from "./AgentGraphPreview";
import FlowVersionDiff from "./FlowVersionDiff";

export default function FlowDetails({ params }) {
  const [flow, setFlow] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [activeVersionModalOpened, activeVersionModal] = useDisclosure(false); // active version m
  const [opened, { open, close }] = useDisclosure(false); // run modal
  const [diffModalOpened, diffModal] = useDisclosure(false); // diff modal
  const [baseSettingsOpened, baseSettingsToggle] = useDisclosure(false);
  const [forceReload, setForceReload] = useState(0);

  const {
    selectedVersions,
    setSelectedVersion,
    flowTemplates,
    setFlowTemplates,
  } = useFlowStore();
  const selectedVersion = selectedVersions[params.flowId];

  const flowEditorRef = useRef();

  const handleVersionSelected = (version) => {
    const formDirty =
      selectedVersion?.isEditable && flowEditorRef.current?.isDirty();
    if (formDirty) {
      if (
        !window.confirm(
          "You have unsaved changes. Are you sure you want to load a different version?"
        )
      ) {
        return;
      }
    }
    setSelectedVersion(params.flowId, version);
  };

  useEffect(() => {
    setSelectedVersion(params.flowId, null);
    getFlow(params.flowId).then((data) => {
      setFlow(data);
    });
    getPromptTemplateVersions(params.flowId).then((data) => {
      setFlowTemplates(data);
      if (data.length > 0) setSelectedVersion(params.flowId, data[0]);
    });
  }, [params.flowId, forceReload, setSelectedVersion, setFlowTemplates]);

  useEffect(() => {
    setTokens(null);
    if (selectedVersion?.id) {
      getParameters(params.flowId, selectedVersion.id)
        .then((data) => {
          setTokens(data);
        })
        // A template with invalid [[parameter]] syntax makes this endpoint 400; api.js already
        // surfaces the message, so just fall back to "no parameters" instead of an unhandled reject.
        .catch(() => setTokens([]));
    }
  }, [selectedVersion, params.flowId]);

  if (!flow)
    return (
      <Center>
        <Loader size="xl" mt="xl" />
      </Center>
    );

  // Compute version status for badge display
  const getVersionStatus = () => {
    if (!selectedVersion) return null;
    const activeEnvs = flow.activeVersions
      ? Object.entries(flow.activeVersions)
          .filter(([, vId]) => vId === selectedVersion.id)
          .map(([env]) => env)
      : [];
    if (activeEnvs.length > 0) return { label: `Active (${activeEnvs.join(", ")})`, color: "green" };
    if (selectedVersion.isEditable) return { label: "Draft", color: "blue" };
    return { label: "Locked", color: "gray" };
  };
  const versionStatus = getVersionStatus();

  const iconStyle = { width: rem(12), height: rem(12) };

  return (
    <Container size="lg">
      <Group justify="space-between" mb="sm">
        <Group>
          <Title order={1}>
            {flow.title}
          </Title>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={baseSettingsToggle.toggle}
            title="Edit flow settings"
          >
            <IconSettings size={18} />
          </ActionIcon>
          {flow.description && (
            <Text size="sm" c="dimmed">{flow.description}</Text>
          )}
        </Group>
      </Group>
      <Collapse expanded={baseSettingsOpened} mb="md">
        <FlowSettingsBase flow={flow} setFlow={setFlow} />
        <FlowDetailSampleRate flow={flow} setFlow={setFlow} />
      </Collapse>
      <Tabs keepMounted={false} variant="outline" defaultValue="prompts">
        <Tabs.List mb="sm">
          <Tabs.Tab
            value="prompts"
            leftSection={<IconMessageCircle style={iconStyle} />}
          >
            Versions
          </Tabs.Tab>
          <Tabs.Tab
            value="analysis"
            leftSection={<IconDashboard style={iconStyle} />}
          >
            Analysis
          </Tabs.Tab>
          <Tabs.Tab
            value="agent-graph"
            leftSection={<IconHierarchy3 style={iconStyle} />}
          >
            Agent Graph
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="analysis">
          <FlowAnalysis flowName={flow.name} />
        </Tabs.Panel>

        <Tabs.Panel value="agent-graph">
          {selectedVersion ? (
            <AgentGraphPreview flowId={flow.id} versionId={selectedVersion.id} />
          ) : (
            <Text c="dimmed" size="sm">
              Select a version to view the agent graph
            </Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="prompts">
          <Grid mt="lg">
            <Grid.Col span={{ base: 12, sm: 4, md: 3 }}>
              <FlowTemplateVersions
                selectedVersion={selectedVersion}
                setSelectedVersion={(version) => handleVersionSelected(version)}
                flow={flow}
                onUnpin={(environment) =>
                  unpinEnvironment(flow.id, environment).then((updated) => {
                    setFlow(updated);
                    notifications.show({
                      title: "Unpinned",
                      message: `'${environment}' is no longer active on this flow`,
                    });
                  })
                }
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8, md: 9 }}>
              {selectedVersion && (
                <>
                  <Group mb="md" justify="space-between">
                    <Group>
                      {versionStatus && (
                        <Badge variant="light" color={versionStatus.color}>
                          {versionStatus.label}
                        </Badge>
                      )}
                      <Button
                        variant="light"
                        onClick={() =>
                          forkVersion(flow.id, selectedVersion.id).then(
                            (newVersion) => {
                              setFlowTemplates([newVersion, ...flowTemplates]);
                              console.debug(
                                "setting new selected version",
                                newVersion
                              );
                              handleVersionSelected(newVersion);
                            }
                          )
                        }
                        leftSection={<IconGitBranch stroke={1.2} />}
                      >
                        New version
                      </Button>

                      <Button
                        variant="light"
                        onClick={async () => {
                          if (flowEditorRef.current?.isDirty()) {
                            try {
                              await flowEditorRef.current.save();
                            } catch {
                              notifications.show({
                                title: "Cannot activate",
                                message:
                                  "Please fix validation errors before activating.",
                                color: "red",
                              });
                              return;
                            }
                          }
                          activeVersionModal.open();
                        }}
                        leftSection={<IconDirections stroke={1.2} />}
                      >
                        Activate
                      </Button>

                      <Button
                        variant="light"
                        onClick={() => {
                          diffModal.open();
                        }}
                        leftSection={<IconGitCompare stroke={1.2} />}
                      >
                        Compare
                      </Button>
                    </Group>

                    <Button
                      variant="light"
                      onClick={open}
                      leftSection={<IconPlayerSkipForwardFilled stroke={1.2} />}
                    >
                      Try
                    </Button>
                  </Group>
                  <FlowVersionEditor
                    ref={flowEditorRef}
                    flow={selectedVersion}
                    parentFlow={flow}
                  />
                </>
              )}
            </Grid.Col>
          </Grid>
        </Tabs.Panel>
      </Tabs>
      <Modal
        size="xl"
        closeOnClickOutside={false}
        opened={opened}
        p="xl"
        onClose={close}
        title="Run Template Flow"
      >
        {/* <FlowParams tokens={tokens} /> */}
        <FlowRun flow={flow} version={selectedVersion} tokens={tokens} />
      </Modal>

      <Modal
        size="sm"
        closeOnClickOutside={false}
        opened={activeVersionModalOpened}
        p="xl"
        onClose={activeVersionModal.close}
        title="Activate Version"
      >
        <FlowEnvironments
          flow={flow}
          version={selectedVersion}
          close={activeVersionModal.close}
          reload={() => setForceReload(forceReload + 1)}
        />
      </Modal>

      <Modal
        size="xl"
        opened={diffModalOpened}
        onClose={diffModal.close}
        title="Compare Versions"
        styles={{ body: { minHeight: 500 } }}
      >
        <FlowVersionDiff flowTemplates={flowTemplates} flow={flow} selectedVersion={selectedVersion} />
      </Modal>
    </Container>
  );
}
